import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { LANG_NAMES, langDir, langFont } from '../lib/format';
import {
  Card, ErrorNote, Field, Hash, Loading, Modal, PageHead, Pill, useResource, useToast,
} from '../components/ui';

/**
 * Notice editor.
 *
 * One form per language, one checklist for the version. The checklist is the
 * publish gate explained: every item names the section it depends on, reflects
 * what is typed right now (saved or not), and scrolls to the fields that are
 * still empty. Publishing is one-way and asks before it happens.
 */

interface NoticeContent {
  title: string;
  intro: string;
  dataItemsIntro: string;
  rights: { heading: string; body: string; items: string[] };
  withdrawal: { heading: string; body: string; url: string };
  grievance: { heading: string; officerName: string; email: string; phone: string; address: string };
  boardComplaint: { heading: string; body: string; url: string };
  additionalSections: Array<{ heading: string; body: string }>;
  purposes?: Array<{ purposeId: string; name: string; description: string; dataCategories: string[]; retentionDays: number | null }>;
}

interface Variant {
  lang: string;
  content: NoticeContent;
  contentHash: string;
  translationStatus?: 'machine_drafted' | 'reviewed' | 'approved';
  /** What stands between this language and being served. Empty when nothing does. */
  gaps?: string[];
}

interface Purpose {
  id: string;
  status: string;
  current: { name: string; dataCategories: string[]; basis: string };
}

interface NoticeVersionRow {
  version: number; status: string; title: string; purposeIds: string[]; languages: string[]; publishedAt: string | null;
  /** Retention and backup statements, one per system of record; shared by every language. */
  retention: Statement[];
}

interface Statement { system: string; liveRetention: string; backupRetention: string; basis: string }

const BLANK_STATEMENT: Statement = { system: '', liveRetention: '', backupRetention: '', basis: '' };

const STATUS_LABEL: Record<string, string> = {
  machine_drafted: 'Machine draft, not reviewed',
  reviewed: 'Reviewed',
  approved: 'Approved',
};

type Section = 'purposes' | 'retention' | 'intro' | 'rights' | 'withdrawal' | 'grievance' | 'board' | 'languages';

interface S5Check {
  block: string;
  section: string;
  ok: boolean;
  /** Passed with something still worth seeing; the detail says what. */
  warn?: boolean;
  detail?: string;
  target: Section;
  failing?: Array<{ lang: string; fields: string[] }>;
}

/**
 * The same list the API publishes against (REQUIRED_FIELDS in the notices
 * module). Kept here so the form can flag a field the moment it is emptied,
 * without a round trip.
 */
const REQUIRED: ReadonlyArray<{ path: string; label: string; section: Section }> = [
  { path: 'title', label: 'Title', section: 'intro' },
  { path: 'intro', label: 'Introduction', section: 'intro' },
  { path: 'rights.heading', label: 'Heading', section: 'rights' },
  { path: 'rights.body', label: 'How rights are exercised', section: 'rights' },
  { path: 'withdrawal.heading', label: 'Heading', section: 'withdrawal' },
  { path: 'withdrawal.body', label: 'How consent is withdrawn', section: 'withdrawal' },
  { path: 'withdrawal.url', label: 'Preference centre link', section: 'withdrawal' },
  { path: 'grievance.heading', label: 'Heading', section: 'grievance' },
  { path: 'grievance.officerName', label: 'Name', section: 'grievance' },
  { path: 'grievance.email', label: 'Email', section: 'grievance' },
  { path: 'boardComplaint.heading', label: 'Heading', section: 'board' },
  { path: 'boardComplaint.body', label: 'How to complain', section: 'board' },
];

const SECTION_TITLE: Record<Section, string> = {
  purposes: 'Purposes covered',
  retention: 'Where the data is kept',
  intro: 'Title and introduction',
  rights: 'Rights of the person',
  withdrawal: 'Withdrawing consent',
  grievance: 'Grievance officer',
  board: 'Data Protection Board',
  languages: 'Languages',
};

function read(c: NoticeContent, path: string): string {
  return String(path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), c) ?? '');
}

/** Required fields this content is missing, by path. */
function gapsOf(c: NoticeContent | null): string[] {
  if (!c) return REQUIRED.map((f) => f.path);
  return REQUIRED.filter((f) => {
    const v = read(c, f.path).trim();
    if (!v) return true;
    if (f.path === 'grievance.email') return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    return false;
  }).map((f) => f.path);
}

const BLANK: NoticeContent = {
  title: '',
  intro: '',
  dataItemsIntro: 'The personal data we collect for each purpose:',
  rights: {
    heading: 'Your rights',
    body: '',
    items: [
      'Right to access a summary of your personal data (S.11)',
      'Right to correction and erasure (S.12)',
      'Right of grievance redressal (S.13)',
      'Right to nominate (S.14)',
    ],
  },
  withdrawal: { heading: 'Withdrawing your consent', body: '', url: '' },
  grievance: { heading: 'Grievance officer', officerName: '', email: '', phone: '', address: '' },
  boardComplaint: {
    heading: 'Complaining to the Data Protection Board',
    body: 'If we do not resolve your grievance, you may complain to the Data Protection Board of India.',
    url: 'https://www.dpb.gov.in',
  },
  additionalSections: [],
};

const EIGHTH_SCHEDULE = Object.keys(LANG_NAMES);

function jump(section: Section) {
  document.getElementById(`sec-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function NoticeEditor() {
  const { noticeId = '', version: versionParam = '1' } = useParams();
  const version = Number(versionParam);
  const writable = can(getUser(), 'notices:edit');
  const publishable = can(getUser(), 'notices:publish');
  const toast = useToast();
  const navigate = useNavigate();

  const [lang, setLang] = useState('en');
  const [draft, setDraft] = useState<NoticeContent | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showGaps, setShowGaps] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingLang, setAddingLang] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [statements, setStatements] = useState<Statement[] | null>(null);
  const [savingStatements, setSavingStatements] = useState(false);

  const notice = useResource<{ notice: { id: string; key: string; versions: NoticeVersionRow[] } }>(
    () => api.get(`/v1/notices/${noticeId}`),
    [noticeId, nonce],
  );
  const variants = useResource<{ variants: Variant[] }>(
    () => api.get(`/v1/notices/${noticeId}/versions/${version}/variants`),
    [noticeId, version, nonce],
  );
  const checks = useResource<{ ok: boolean; checks: S5Check[]; variants: Array<{ lang: string; gaps: string[] }> }>(
    () => api.get(`/v1/notices/${noticeId}/versions/${version}/check`),
    [noticeId, version, nonce],
  );
  const purposes = useResource<{ purposes: Purpose[] }>(() => api.get('/v1/purposes'), [nonce]);
  const settings = useResource<{ languages: string[] }>(() => api.get('/v1/settings/languages'));
  const offered = settings.data?.languages ?? ['en'];

  const versionRow = notice.data?.notice.versions.find((v) => v.version === version);
  const editable = writable && versionRow?.status === 'draft';
  const savedStatements = versionRow?.retention ?? [];
  const shownStatements = statements ?? savedStatements;
  const statementsDirty = statements !== null;
  const languages = useMemo(() => variants.data?.variants.map((v) => v.lang) ?? [], [variants.data]);
  const exists = languages.includes(lang);

  useEffect(() => {
    const found = variants.data?.variants.find((v) => v.lang === lang);
    // A draft version with nothing in a language yet shows the blank form
    // straight away; the first save creates the variant.
    setDraft(found ? structuredClone(found.content) : (editable && lang === 'en' ? structuredClone(BLANK) : null));
    setDirty(false);
    setShowGaps(false);
  }, [variants.data, lang, editable]);

  const liveGaps = useMemo(() => gapsOf(draft), [draft]);

  /**
   * The checklist as it stands right now: the API's view for every other
   * language, and what is typed in this one, saved or not.
   */
  const items = useMemo(() => {
    if (!checks.data) return [];
    return checks.data.checks.map((c) => {
      if (!['intro', 'rights', 'withdrawal', 'grievance', 'board'].includes(c.target)) return { ...c, mine: [] as string[] };
      const paths = REQUIRED.filter((f) => f.section === c.target).map((f) => f.path);
      const mine = draft ? liveGaps.filter((p) => paths.includes(p)) : (exists ? [] : paths);
      const others = (c.failing ?? []).filter((f) => f.lang !== lang);
      return { ...c, mine, failing: others, ok: mine.length === 0 && others.length === 0 && (exists || dirty || Boolean(draft)) };
    });
  }, [checks.data, liveGaps, lang, draft, exists, dirty]);

  const outstanding = items.filter((c) => !c.ok).length;

  async function save() {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/v1/notices/${noticeId}/versions/${version}/variants/${lang}`, draft);
      toast(`${LANG_NAMES[lang] ?? lang} saved.`, 'ok');
      setDirty(false);
      setNonce((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function saveStatements() {
    if (!statements) return;
    setSavingStatements(true);
    setError(null);
    try {
      await api.put(`/v1/notices/${noticeId}/versions/${version}/retention`, { statements });
      toast('Retention statements saved.', 'ok');
      setStatements(null);
      setNonce((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingStatements(false);
    }
  }

  const editStatement = (i: number, patch: Partial<Statement>) =>
    setStatements((shownStatements).map((st, j) => (j === i ? { ...st, ...patch } : st)));

  async function publish() {
    setPublishing(true);
    setError(null);
    try {
      const res = await api.post<{
        variants: Array<{ lang: string; contentHash: string }>;
        withheld?: Array<{ lang: string; reasons: string[] }>;
      }>(`/v1/notices/${noticeId}/versions/${version}/publish`);
      const held = res.withheld?.length
        ? ` Not served: ${res.withheld.map((w) => LANG_NAMES[w.lang] ?? w.lang).join(', ')}.`
        : '';
      toast(`Published v${version} in ${res.variants.map((v) => LANG_NAMES[v.lang] ?? v.lang).join(', ')}.${held}`, held ? 'bad' : 'ok');
      setConfirming(false);
      setNonce((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
      setConfirming(false);
    } finally {
      setPublishing(false);
    }
  }

  /** A published version is frozen, so a new language starts a new draft version. */
  async function addLanguageOnNewVersion() {
    // If a draft is already open, go there rather than opening a second one.
    const draftRow = notice.data?.notice.versions.find((v) => v.status === 'draft');
    if (draftRow) {
      navigate(`/notices/${noticeId}/versions/${draftRow.version}?add-language=1`);
      return;
    }
    try {
      const res = await api.post<{ version: { version: number } }>(`/v1/notices/${noticeId}/versions`);
      toast(`Draft v${res.version.version} opened from v${version}. Add the language here.`, 'ok');
      navigate(`/notices/${noticeId}/versions/${res.version.version}?add-language=1`);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function addLanguage(code: string) {
    const seed = variants.data?.variants.find((v) => v.lang === 'en')?.content ?? BLANK;
    try {
      await api.put(`/v1/notices/${noticeId}/versions/${version}/variants/${code}`, structuredClone(seed));
      setAddingLang(false);
      setLang(code);
      setNonce((n) => n + 1);
      toast(`${LANG_NAMES[code] ?? code} added. Translate each field, then mark it reviewed.`, 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  // Arrived from a published version's "Add language": open the picker at once.
  useEffect(() => {
    if (editable && new URLSearchParams(window.location.hash.split('?')[1] ?? '').has('add-language')) {
      setAddingLang(true);
      window.history.replaceState(null, '', `#/notices/${noticeId}/versions/${version}`);
    }
  }, [editable, noticeId, version]);

  async function setPurposes(ids: string[]) {
    try {
      await api.put(`/v1/notices/${noticeId}/versions/${version}/purposes`, { purposeIds: ids });
      setNonce((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const set = (patch: Partial<NoticeContent>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setDirty(true);
  };

  const missing = (path: string) => (showGaps || exists) && liveGaps.includes(path);
  const flag = (path: string) => (missing(path) ? <div className="field-error">{path === 'grievance.email' && read(draft!, path) ? 'Enter a valid email address.' : 'Required before publishing.'}</div> : null);
  const cls = (path: string) => (missing(path) ? 'invalid' : undefined);

  const linked = versionRow?.purposeIds ?? [];
  const activePurposes = (purposes.data?.purposes ?? []).filter((p) => p.status === 'active' || linked.includes(p.id));
  const publishBlocked = !checks.data?.ok || dirty || statementsDirty || !editable;
  const publishHint = !editable ? '' : dirty || statementsDirty ? 'Save before publishing.' : outstanding ? `${outstanding} item${outstanding === 1 ? '' : 's'} to complete.` : '';

  return (
    <>
      <PageHead eyebrow="Notices" backTo="/notices" title={versionRow?.title ?? 'Notice'}>
        <Pill tone={versionRow?.status === 'published' ? 'ok' : versionRow?.status === 'draft' ? 'warn' : undefined}>
          {versionRow ? `${versionRow.status === 'draft' ? 'Draft' : versionRow.status === 'published' ? 'Published' : 'Superseded'} v${version}` : ''}
        </Pill>
        <Link className="btn" to="/notices">All notices</Link>
        {editable && (
          <>
            <button onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button
              className="primary"
              onClick={() => (publishBlocked ? setShowGaps(true) : setConfirming(true))}
              disabled={publishing}
              aria-disabled={publishBlocked}
              title={publishHint || undefined}
              style={publishBlocked ? { opacity: 0.55 } : undefined}
            >
              Publish v{version}
            </button>
          </>
        )}
      </PageHead>

      <ErrorNote error={error ?? notice.error ?? variants.error} />

      <div className="tabs">
        {[...new Set(['en', ...languages])].map((l) => {
          const g = checks.data?.variants.find((v) => v.lang === l)?.gaps ?? [];
          const complete = l === lang ? liveGaps.length === 0 : (languages.includes(l) && g.length === 0);
          return (
            <button
              key={l}
              className={l === lang ? 'active' : ''}
              onClick={() => setLang(l)}
              lang={l}
              dir={langDir(l)}
              style={{ fontFamily: langFont(l) }}
              title={complete ? 'Complete' : languages.includes(l) || l === lang ? 'Fields still empty' : 'Not started'}
            >
              {LANG_NAMES[l] ?? l}
              {!complete && <span className="tab-flag" aria-label="incomplete"> ●</span>}
            </button>
          );
        })}
        {editable && offered.filter((l) => !languages.includes(l) && l !== 'en').map((l) => (
          <button
            key={l}
            className="muted"
            onClick={() => addLanguage(l)}
            lang={l}
            dir={langDir(l)}
            style={{ fontFamily: langFont(l), opacity: 0.7 }}
            title={`Add ${LANG_NAMES[l] ?? l}, starting from the English text`}
          >
            + {LANG_NAMES[l] ?? l}
          </button>
        ))}
        {editable && (
          <button onClick={() => setAddingLang(true)} style={{ marginLeft: 'auto' }}>+ Add language</button>
        )}
        {!editable && writable && versionRow?.status === 'published' && (
          <button onClick={addLanguageOnNewVersion} style={{ marginLeft: 'auto' }} title="Opens the next draft version with this text">
            + Add language in a new version
          </button>
        )}
      </div>

      <div className="grid cols-2" style={{ gridTemplateColumns: 'minmax(0, 340px) minmax(0, 1fr)', alignItems: 'start' }}>
        <div style={{ position: 'sticky', top: 12, maxHeight: 'calc(100vh - 24px)', overflowY: 'auto' }}>
          <Card title={versionRow?.status === 'draft' ? 'Publish checklist' : 'Contents'}>
            {checks.loading && !checks.data && <Loading what="Checking" />}
            {items.map((c) => (
              <button
                key={c.block}
                type="button"
                className="check-item"
                onClick={() => { setShowGaps(true); if (c.target !== 'languages') jump(c.target); }}
                aria-label={`${c.block}: ${c.ok ? 'complete' : 'incomplete'}`}
              >
                <span className={`check-mark ${c.ok ? 'ok' : ''}`} aria-hidden style={c.ok && c.warn ? { background: 'var(--warn, #B54708)' } : undefined}>{c.ok ? (c.warn ? '!' : '✓') : ''}</span>
                <span className="check-body">
                  <span className="check-text">{c.block}</span>
                  <span className="small muted">{c.section}</span>
                  {(!c.ok || c.warn) && c.detail && <span className="small check-why">{c.detail}</span>}
                  {!c.ok && c.mine.length > 0 && (
                    <span className="small check-why">
                      {LANG_NAMES[lang] ?? lang}: {c.mine.map((p) => REQUIRED.find((f) => f.path === p)?.label ?? p).join(', ')}
                    </span>
                  )}
                  {(c.failing ?? []).map((f) => (
                    <span key={f.lang} className="small check-why">
                      {LANG_NAMES[f.lang] ?? f.lang}: {f.fields.map((p) => REQUIRED.find((x) => x.path === p)?.label ?? p).join(', ')}
                    </span>
                  ))}
                </span>
              </button>
            ))}
            {checks.data && editable && (
              <div className={`note ${outstanding === 0 && !dirty ? 'ok' : 'warn'}`} style={{ marginTop: 10 }}>
                {outstanding === 0
                  ? (dirty ? 'All items complete. Save, then publish.' : 'All items complete. Publishing freezes this version.')
                  : `${outstanding} item${outstanding === 1 ? '' : 's'} to complete. Select one to go to it.`}
              </div>
            )}
          </Card>

          {variants.data && variants.data.variants.length > 0 && (
            <Card title="Languages">
              {variants.data.variants.map((v) => (
                <div key={v.lang} style={{ padding: '6px 0' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ flex: 1 }}>{LANG_NAMES[v.lang] ?? v.lang}</span>
                    {editable ? (
                      <select
                        aria-label={`Translation review, ${LANG_NAMES[v.lang] ?? v.lang}`}
                        value={v.translationStatus ?? 'reviewed'}
                        style={{ width: 'auto' }}
                        onChange={async (e) => {
                          try {
                            await api.post(
                              `/v1/notices/${noticeId}/versions/${version}/variants/${v.lang}/status`,
                              { status: e.target.value },
                            );
                            setNonce((n) => n + 1);
                          } catch (err) {
                            setError((err as Error).message);
                          }
                        }}
                      >
                        {Object.entries(STATUS_LABEL).map(([k, label]) => (
                          <option key={k} value={k}>{label}</option>
                        ))}
                      </select>
                    ) : (
                      <Pill tone={v.gaps?.length ? 'bad' : 'ok'}>
                        {v.gaps?.length ? 'Not served' : 'Served'}
                      </Pill>
                    )}
                  </div>
                  {(v.gaps ?? []).map((g) => (
                    <div key={g} className="small" style={{ color: 'var(--bad, #B42318)', marginTop: 4 }}>{g}</div>
                  ))}
                </div>
              ))}
            </Card>
          )}

          <Card title="Versions">
            {notice.data?.notice.versions.map((v) => (
              <div key={v.version} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0' }}>
                <Link to={`/notices/${noticeId}/versions/${v.version}`}>Version {v.version}</Link>
                <Pill tone={v.status === 'published' ? 'ok' : v.status === 'draft' ? 'warn' : undefined}>
                  {v.status === 'draft' ? 'Draft' : v.status === 'published' ? 'Published' : 'Superseded'}
                </Pill>
                <span className="small muted" style={{ marginLeft: 'auto' }}>
                  {v.publishedAt ? v.publishedAt.slice(0, 10) : v.languages.map((l) => (LANG_NAMES[l] ?? l).split(' ').pop()).join(', ')}
                </span>
              </div>
            ))}
          </Card>

          {versionRow && versionRow.status !== 'draft' && variants.data && (
            <Card title="Record">
              {variants.data.variants.map((v) => (
                <div key={v.lang} className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                  <span style={{ minWidth: 90 }}>{(LANG_NAMES[v.lang] ?? v.lang).split(' ').pop()}</span>
                  <Hash value={v.contentHash} head={10} />
                </div>
              ))}
            </Card>
          )}
        </div>

        <div>
          <Card title={SECTION_TITLE.purposes} >
            <div id="sec-purposes" />
            {purposes.loading && !purposes.data && <Loading what="Loading purposes" />}
            {purposes.data && activePurposes.length === 0 && (
              <div className="note warn">No active purposes. <Link to="/purposes">Add one under Purposes</Link> first.</div>
            )}
            {activePurposes.map((p) => {
              const on = linked.includes(p.id);
              const noData = on && p.current.dataCategories.length === 0;
              return (
                <label key={p.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0', fontWeight: 400 }}>
                  <input
                    type="checkbox" style={{ width: 'auto', marginTop: 3 }}
                    checked={on}
                    disabled={!editable}
                    onChange={(e) => setPurposes(e.target.checked ? [...linked, p.id] : linked.filter((x) => x !== p.id))}
                  />
                  <span>
                    {p.current.name}
                    {p.status !== 'active' && <> <Pill tone="bad">{p.status}</Pill></>}
                    <div className="small muted">
                      {p.current.basis === 'consent' ? 'Consent' : 'Legitimate use'}
                      {p.current.dataCategories.length ? ` · ${p.current.dataCategories.join(', ')}` : ''}
                    </div>
                    {noData && (
                      <div className="field-error">No data categories listed. <Link to="/purposes">Add them under Purposes</Link>.</div>
                    )}
                  </span>
                </label>
              );
            })}
            {(showGaps || exists) && linked.length === 0 && activePurposes.length > 0 && (
              <div className="field-error">Choose at least one purpose.</div>
            )}
          </Card>

          <Card title={SECTION_TITLE.retention}>
            <div id="sec-retention" />
            <div className="small muted" style={{ marginBottom: 8 }}>
              One row per system of record: how long the live copy is kept, how long its backups are kept, and the basis.
              Shown in every language of the notice under "Where your data is kept", and quoted in the answer to an erasure request.
            </div>
            {shownStatements.length > 0 && editable && (
              <div style={{ display: 'grid', gap: 10 }}>
                {shownStatements.map((st, i) => (
                  <div key={i} style={{ border: '1px solid var(--line, #E4E7EC)', borderRadius: 8, padding: '10px 12px' }}>
                    <div className="row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
                      <Field label="System">
                        <input type="text" value={st.system} placeholder="SAP" onChange={(e) => editStatement(i, { system: e.target.value })} />
                      </Field>
                      <Field label="Live copy kept for">
                        <input type="text" value={st.liveRetention} placeholder="3 years after the last order" onChange={(e) => editStatement(i, { liveRetention: e.target.value })} />
                      </Field>
                      <Field label="Backups kept for">
                        <input type="text" value={st.backupRetention} placeholder="1 year, or blank" onChange={(e) => editStatement(i, { backupRetention: e.target.value })} />
                      </Field>
                      <Field label="Basis">
                        <input type="text" value={st.basis} placeholder="Statutory audit records" onChange={(e) => editStatement(i, { basis: e.target.value })} />
                      </Field>
                    </div>
                    <button className="small" onClick={() => setStatements(shownStatements.filter((_, j) => j !== i))} aria-label={`Remove ${st.system || 'row'}`}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            {shownStatements.length > 0 && !editable && (
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr><th>System</th><th>Live copy kept for</th><th>Backups kept for</th><th>Basis</th></tr>
                  </thead>
                  <tbody>
                    {shownStatements.map((st, i) => (
                      <tr key={i}>
                        <td><strong>{st.system}</strong></td>
                        <td className="small">{st.liveRetention}</td>
                        <td className="small">{st.backupRetention || 'No backup named'}</td>
                        <td className="small">{st.basis}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {shownStatements.length === 0 && (() => {
              const check = checks.data?.checks.find((c) => c.target === 'retention');
              const gap = check ? !check.ok || check.warn : false;
              return (
                <div className={`note ${gap ? 'warn' : ''}`}>
                  No system of record listed.
                  {check && !check.ok ? ' A covered purpose keeps data for a set period and this organisation requires the statement, so at least one row is needed before publishing.' : ''}
                  {check?.ok && check.warn ? ' A covered purpose keeps data for a set period. Publishing is not blocked; the setting is under Security, record retention.' : ''}
                </div>
              );
            })()}
            {editable && (
              <>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button style={{ whiteSpace: 'nowrap' }} onClick={() => setStatements([...shownStatements, { ...BLANK_STATEMENT }])}>Add row</button>
                  <button className="primary" style={{ whiteSpace: 'nowrap' }} onClick={saveStatements} disabled={!statementsDirty || savingStatements}>
                    {savingStatements ? 'Saving' : 'Save statements'}
                  </button>
                  {statementsDirty && <button onClick={() => setStatements(null)}>Discard</button>}
                </div>
                <div className="small muted" style={{ marginTop: 8 }}>
                  A backup period written as a count, such as "1 year" or "90 days", is turned into a date in the erasure response.
                </div>
              </>
            )}
          </Card>

          {!draft ? (
            <Card>
              <div className="empty">
                <div>Nothing in {LANG_NAMES[lang] ?? lang} yet.</div>
                {editable && (
                  <button className="primary" style={{ marginTop: 14 }} onClick={() => setAddingLang(true)}>
                    Add {LANG_NAMES[lang] ?? lang}
                  </button>
                )}
              </div>
            </Card>
          ) : (
            <Card title={`Text · ${LANG_NAMES[lang] ?? lang}`}>
              <fieldset
                disabled={!editable}
                style={{ border: 0, padding: 0, margin: 0, fontFamily: langFont(lang) }}
                lang={lang}
                dir={langDir(lang)}
              >
                <h2 className="section" id="sec-intro" style={{ marginTop: 0 }}>{SECTION_TITLE.intro}</h2>
                <Field label="Title">
                  <input className={cls('title')} type="text" value={draft.title} onChange={(e) => set({ title: e.target.value })} />
                  {flag('title')}
                </Field>
                <Field label="Introduction" help="Who you are and why you are collecting personal data, in plain words.">
                  <textarea className={cls('intro')} value={draft.intro} onChange={(e) => set({ intro: e.target.value })} />
                  {flag('intro')}
                </Field>
                <Field label="Heading above the data list" help="The list itself comes from the purposes above.">
                  <input type="text" value={draft.dataItemsIntro} onChange={(e) => set({ dataItemsIntro: e.target.value })} />
                </Field>

                <h2 className="section" id="sec-rights">{SECTION_TITLE.rights} <span className="small muted" style={{ fontWeight: 400 }}>S.5(1)(iii)</span></h2>
                <Field label="Heading">
                  <input className={cls('rights.heading')} type="text" value={draft.rights.heading} onChange={(e) => set({ rights: { ...draft.rights, heading: e.target.value } })} />
                  {flag('rights.heading')}
                </Field>
                <Field label="How rights are exercised" help="Where the person writes or clicks, and how long you take to answer.">
                  <textarea className={cls('rights.body')} value={draft.rights.body} onChange={(e) => set({ rights: { ...draft.rights, body: e.target.value } })} />
                  {flag('rights.body')}
                </Field>
                <Field label="Rights listed" help="One per line.">
                  <textarea
                    value={draft.rights.items.join('\n')}
                    onChange={(e) => set({ rights: { ...draft.rights, items: e.target.value.split('\n').filter(Boolean) } })}
                  />
                </Field>

                <h2 className="section" id="sec-withdrawal">{SECTION_TITLE.withdrawal} <span className="small muted" style={{ fontWeight: 400 }}>S.6(4)</span></h2>
                <Field label="Heading">
                  <input className={cls('withdrawal.heading')} type="text" value={draft.withdrawal.heading} onChange={(e) => set({ withdrawal: { ...draft.withdrawal, heading: e.target.value } })} />
                  {flag('withdrawal.heading')}
                </Field>
                <Field label="How consent is withdrawn" help="As easy as giving consent (S.6(4)).">
                  <textarea className={cls('withdrawal.body')} value={draft.withdrawal.body} onChange={(e) => set({ withdrawal: { ...draft.withdrawal, body: e.target.value } })} />
                  {flag('withdrawal.body')}
                </Field>
                <Field label="Preference centre link" help="Where the person changes or withdraws consent. Also shown on the consent widget.">
                  <input className={cls('withdrawal.url')} type="url" value={draft.withdrawal.url} onChange={(e) => set({ withdrawal: { ...draft.withdrawal, url: e.target.value } })} />
                  {flag('withdrawal.url')}
                </Field>

                <h2 className="section" id="sec-grievance">{SECTION_TITLE.grievance} <span className="small muted" style={{ fontWeight: 400 }}>S.5(1)(iii)</span></h2>
                <Field label="Heading">
                  <input className={cls('grievance.heading')} type="text" value={draft.grievance.heading} onChange={(e) => set({ grievance: { ...draft.grievance, heading: e.target.value } })} />
                  {flag('grievance.heading')}
                </Field>
                <div className="row">
                  <Field label="Name">
                    <input className={cls('grievance.officerName')} type="text" value={draft.grievance.officerName} onChange={(e) => set({ grievance: { ...draft.grievance, officerName: e.target.value } })} />
                    {flag('grievance.officerName')}
                  </Field>
                  <Field label="Email">
                    <input className={cls('grievance.email')} type="email" value={draft.grievance.email} onChange={(e) => set({ grievance: { ...draft.grievance, email: e.target.value } })} />
                    {flag('grievance.email')}
                  </Field>
                  <Field label="Phone" help="Optional.">
                    <input type="text" value={draft.grievance.phone} onChange={(e) => set({ grievance: { ...draft.grievance, phone: e.target.value } })} />
                  </Field>
                </div>
                <Field label="Postal address" help="Optional.">
                  <input type="text" value={draft.grievance.address} onChange={(e) => set({ grievance: { ...draft.grievance, address: e.target.value } })} />
                </Field>

                <h2 className="section" id="sec-board">{SECTION_TITLE.board} <span className="small muted" style={{ fontWeight: 400 }}>S.5(1)(iv)</span></h2>
                <Field label="Heading">
                  <input className={cls('boardComplaint.heading')} type="text" value={draft.boardComplaint.heading} onChange={(e) => set({ boardComplaint: { ...draft.boardComplaint, heading: e.target.value } })} />
                  {flag('boardComplaint.heading')}
                </Field>
                <Field label="How to complain">
                  <textarea className={cls('boardComplaint.body')} value={draft.boardComplaint.body} onChange={(e) => set({ boardComplaint: { ...draft.boardComplaint, body: e.target.value } })} />
                  {flag('boardComplaint.body')}
                </Field>
                <Field label="Board website">
                  <input type="url" value={draft.boardComplaint.url} onChange={(e) => set({ boardComplaint: { ...draft.boardComplaint, url: e.target.value } })} />
                </Field>
              </fieldset>

              {editable && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                  <button className="primary" onClick={save} disabled={!dirty || saving}>
                    {saving ? 'Saving…' : exists ? 'Save' : `Save ${LANG_NAMES[lang] ?? lang}`}
                  </button>
                  <button onClick={() => { setNonce((n) => n + 1); setDirty(false); }} disabled={!dirty}>
                    Discard changes
                  </button>
                  {liveGaps.length > 0 && (
                    <span className="small muted" style={{ marginInlineStart: 'auto' }}>
                      {liveGaps.length} required field{liveGaps.length === 1 ? '' : 's'} empty
                    </span>
                  )}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {addingLang && (
        <AddLanguage
          existing={languages}
          offered={offered}
          onClose={() => setAddingLang(false)}
          onAdd={addLanguage}
        />
      )}

      {confirming && (
        <Modal
          title={`Publish version ${version}`}
          onClose={() => setConfirming(false)}
          footer={
            <>
              <button onClick={() => setConfirming(false)} disabled={publishing}>Cancel</button>
              <button className="primary" onClick={publish} disabled={publishing || !publishable}>{publishing ? 'Publishing…' : 'Publish'}</button>
            </>
          }
        >
          <p>
            Version {version} goes live in {languages.map((l) => (LANG_NAMES[l] ?? l).split(' ').pop()).join(', ')}
            {' '}and replaces the current published version for new consents.
          </p>
          <p className="small muted">
            A published version cannot be edited. Changes after this go into a new version, and consents already taken stay bound to the version they were shown.
          </p>
        </Modal>
      )}

      {!editable && versionRow && versionRow.status !== 'draft' && (
        <div className="note" style={{ marginTop: 16 }}>
          Published {versionRow.publishedAt?.slice(0, 10) ?? ''}. This version cannot be edited; a change opens a new version.
          {' '}
          <button className="small" style={{ marginLeft: 8 }} onClick={() => navigate('/notices')}>Back to notices</button>
        </div>
      )}
    </>
  );
}

function AddLanguage({
  existing, offered, onClose, onAdd,
}: {
  existing: string[];
  offered: string[];
  onClose: () => void;
  onAdd: (code: string) => void;
}) {
  const offeredLeft = offered.filter((l) => !existing.includes(l) && l !== 'en');
  const others = EIGHTH_SCHEDULE.filter((l) => !existing.includes(l) && !offered.includes(l) && l !== 'en');
  const [code, setCode] = useState(offeredLeft[0] ?? others[0] ?? '');

  return (
    <Modal
      title="Add a language"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => onAdd(code)} disabled={!code}>Add</button>
        </>
      }
    >
      <Field
        label="Language"
        help={offeredLeft.length
          ? 'Languages the organisation offers are listed first.'
          : 'Every offered language is already on this notice. Languages below are not offered yet; set that under Users & access › Languages offered.'}
      >
        <select value={code} onChange={(e) => setCode(e.target.value)}>
          {offeredLeft.length > 0 && (
            <optgroup label="Offered">
              {offeredLeft.map((l) => <option key={l} value={l}>{LANG_NAMES[l] ?? l}</option>)}
            </optgroup>
          )}
          {others.length > 0 && (
            <optgroup label="Not offered yet">
              {others.map((l) => <option key={l} value={l}>{LANG_NAMES[l] ?? l}</option>)}
            </optgroup>
          )}
        </select>
      </Field>
      <div className="small muted">
        <Link to="/settings">Change the languages offered</Link>
      </div>
    </Modal>
  );
}
